// Operaciones › Órdenes de Despacho (consulta). Las entradas del menú Preparación, Manifiestos,
// Facturación de Cargas, Rutas y Rendiciones abren esta misma lista filtrada por el estado que les
// corresponde; el trabajo se hace en la pestaña de la OD (Enter).
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Imprimir } from '../comun/Imprimir';
import { fecha } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import {
  NOMBRE_ESTADO_OD,
  PASOS_OD,
  useOrdenes,
  type EstadoOd,
  type OdListada,
  type ProductoManifiesto,
} from './datos';
import {
  CargaConsolidadaImpresa,
  consolidarManifiestos,
  csvCargaConsolidada,
  type ProductoConsolidado,
} from './ImpresosOd';

const COLUMNAS_BASE: Columna<OdListada>[] = [
  {
    id: 'n',
    titulo: 'N° ODD',
    celda: (o) => <span className="mono">{o.numero}</span>,
    ancho: '6rem',
  },
  { id: 'fecha', titulo: 'Fecha', celda: (o) => fecha(o.fechaNegocio), ancho: '7rem' },
  {
    id: 'estado',
    titulo: 'Estado',
    celda: (o) => (
      <span className={`insignia ${o.estado === 'ANULADA' ? 'tono-malo' : 'tono-neutro'}`}>
        {NOMBRE_ESTADO_OD[o.estado]}
      </span>
    ),
    ancho: '11rem',
  },
  { id: 'camion', titulo: 'Camión', celda: (o) => o.patente ?? '—', ancho: '7rem' },
  { id: 'nombre', titulo: 'Nombre', celda: (o) => o.nombre ?? '—' },
  { id: 'chofer', titulo: 'Chofer', celda: (o) => o.chofer ?? '—' },
  { id: 'recaudador', titulo: 'Peoneta recaudador', celda: (o) => o.recaudador ?? '—' },
  { id: 'ruta', titulo: 'Ruta', celda: (o) => o.ruta ?? '—', ancho: '7rem' },
  { id: 'notas', titulo: 'Notas', celda: (o) => o.notas, alinear: 'der', ancho: '5rem' },
];

export function OrdenesDespacho({
  estados: etapas,
  alAbrir,
  alNueva,
}: {
  /** Etapas (estados separados por coma) que muestra la entrada del menú; vacío = todas. */
  estados?: string | undefined;
  alAbrir: (o: OdListada) => void;
  alNueva?: () => void;
}) {
  const { empresa, puede } = useSesion();
  const etapa = (etapas ?? '').split(',').filter(Boolean) as EstadoOd[];
  const [estado, setEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const q = useOrdenes(empresa!.id, {
    ...(estado ? { estado } : {}),
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
  });
  const filas = (q.data ?? []).filter((o) => estado || !etapa.length || etapa.includes(o.estado));
  // Carga consolidada de varias OD (como «Consolidar varias ODD» de Random): impresa o en Excel.
  const [sel, setSel] = useState<Set<string>>(new Set());
  const elegidas = filas.filter((o) => sel.has(o.id) && o.estado !== 'ANULADA');
  const [consolidada, setConsolidada] = useState<ProductoConsolidado[] | null>(null);
  const [cargando, setCargando] = useState<'imprimir' | 'excel' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const consolidar = async (para: 'imprimir' | 'excel') => {
    setCargando(para);
    setError(null);
    try {
      const manifiestos = await Promise.all(
        elegidas.map(async (o) => ({
          numero: o.numero,
          productos: (
            await api.get<{ productos: ProductoManifiesto[] }>(
              `/logistica/ordenes/${o.id}/manifiesto`,
              { query: { empresaId: empresa!.id } },
            )
          ).productos,
        })),
      );
      const productos = consolidarManifiestos(manifiestos);
      if (para === 'imprimir') setConsolidada(productos);
      else {
        const numeros = elegidas.map((o) => o.numero);
        const b = new Blob([csvCargaConsolidada(numeros, productos)], {
          type: 'text/csv;charset=utf-8',
        });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `carga-od-${numeros.join('-')}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      setError(e);
    } finally {
      setCargando(null);
    }
  };
  const columnas: Columna<OdListada>[] = [
    {
      id: 'sel',
      titulo: '',
      celda: (o) => (
        <input
          type="checkbox"
          aria-label={`Elegir ODD ${o.numero}`}
          checked={sel.has(o.id)}
          disabled={o.estado === 'ANULADA'}
          onChange={() =>
            setSel((s) => {
              const n = new Set(s);
              if (n.has(o.id)) n.delete(o.id);
              else n.add(o.id);
              return n;
            })
          }
        />
      ),
      ancho: '2rem',
    },
    ...COLUMNAS_BASE,
  ];
  return (
    <div className="pantalla consulta" data-pantalla="ordenes-despacho">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado de la ODD"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">{etapa.length ? 'Esta etapa' : 'Todos'}</option>
            {[...PASOS_OD, 'ANULADA' as EstadoOd].map((e) => (
              <option key={e} value={e}>
                {NOMBRE_ESTADO_OD[e]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        <button
          type="button"
          disabled={!elegidas.length || cargando !== null}
          onClick={() => void consolidar('imprimir')}
          title="Suma por producto las ODD elegidas en una sola hoja de carga"
        >
          {cargando === 'imprimir' ? 'Preparando…' : `Carga consolidada (${elegidas.length})`}
        </button>
        <button
          type="button"
          disabled={!elegidas.length || cargando !== null}
          onClick={() => void consolidar('excel')}
        >
          {cargando === 'excel' ? 'Preparando…' : 'Excel'}
        </button>
        {alNueva && puede('logistica.od.crear') && (
          <button type="button" className="primario" onClick={alNueva}>
            Nueva ODD
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {!!error && <PanelError error={error} />}
      <Grilla
        etiqueta="Órdenes de despacho"
        columnas={columnas}
        filas={filas}
        clave={(o) => o.id}
        cargando={q.isLoading}
        vacio="No hay órdenes de despacho con esos filtros"
        alAbrir={alAbrir}
      />
      {consolidada && (
        <Imprimir alTerminar={() => setConsolidada(null)}>
          <CargaConsolidadaImpresa
            empresa={empresa!.razon_social}
            ordenes={elegidas}
            productos={consolidada}
          />
        </Imprimir>
      )}
    </div>
  );
}
